import { type FC, useCallback, useEffect, useState } from "react";

import type { DashboardSourceControlRepositoryUpsertRequest } from "../../../dashboard-client/types.gen";

import {
  type DiscoveredRepository,
  discoverSourceControlRepositories,
  upsertSourceControlRepository,
} from "../../../api/sourceControl";
import { Alert } from "../../common/Alert";
import { Button } from "../../common/Button";

interface GitHubStepProps {
  isComplete: boolean;
  githubProviderId: string | null;
  onInstall: () => Promise<void>;
  onComplete: () => void;
  onSkip: () => void;
  submitting: boolean;
  error: string | null;
}

export const GitHubStep: FC<GitHubStepProps> = ({
  isComplete,
  githubProviderId,
  onInstall,
  onComplete,
  onSkip,
  submitting,
  error,
}) => {
  if (isComplete) {
    return (
      <RepoSelectionStep
        providerId={githubProviderId}
        onContinue={onComplete}
      />
    );
  }

  return (
    <div className="space-y-4">
      <div>
        <h2 className="font-mono text-sm font-bold uppercase tracking-widest text-white">
          Install the GitHub App
        </h2>
        <p className="mt-1 text-sm leading-6 text-slate-400">
          The Arti GitHub App allows Arti to access your repositories, create
          pull requests, and respond to events. You&apos;ll be redirected to
          GitHub to install the app on your organization.
        </p>
      </div>

      {error && (
        <Alert variant="error" animate>
          {error}
        </Alert>
      )}

      <Button
        onClick={() => void onInstall()}
        loading={submitting}
        loadingText="Redirecting..."
        fullWidth
      >
        Install GitHub App
      </Button>

      <Button onClick={onSkip} variant="ghost" fullWidth>
        Skip for now
      </Button>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Sub-step: Select repositories to add to the allowlist
// ---------------------------------------------------------------------------

const RepoSelectionStep: FC<{
  providerId: string | null;
  onContinue: () => void;
}> = ({ providerId, onContinue }) => {
  const [repos, setRepos] = useState<DiscoveredRepository[]>([]);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [repoError, setRepoError] = useState<string | null>(null);

  const discover = useCallback(async () => {
    if (!providerId) {
      setLoading(false);
      return;
    }
    try {
      setLoading(true);
      setRepoError(null);
      const discovered = await discoverSourceControlRepositories(providerId);
      setRepos(discovered);
      setSelected(new Set(discovered.map((r) => r.id)));
    } catch (error) {
      const message =
        error instanceof Error
          ? error.message
          : "Failed to discover repositories.";
      setRepoError(message);
    } finally {
      setLoading(false);
    }
  }, [providerId]);

  useEffect(() => {
    void discover();
  }, [discover]);

  const toggle = (id: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const toggleAll = () => {
    if (selected.size === repos.length) {
      setSelected(new Set());
    } else {
      setSelected(new Set(repos.map((r) => r.id)));
    }
  };

  const handleSave = async () => {
    if (!providerId || selected.size === 0) {
      onContinue();
      return;
    }

    setSaving(true);
    setRepoError(null);
    try {
      const selectedRepos = repos.filter((r) => selected.has(r.id));
      await Promise.all(
        selectedRepos.map((repo) => {
          const input: DashboardSourceControlRepositoryUpsertRequest = {
            remote_slug: repo.name,
            repository_id: `${providerId}:${repo.id}`,
          };
          return upsertSourceControlRepository(providerId, input);
        }),
      );
      onContinue();
    } catch (error) {
      const message =
        error instanceof Error ? error.message : "Failed to add repositories.";
      setRepoError(message);
      setSaving(false);
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3">
        <div className="inline-grid size-10 place-items-center border border-emerald-500/30 bg-emerald-500/10">
          <span className="font-mono text-sm text-emerald-400">■</span>
        </div>
        <div>
          <h2 className="font-mono text-sm font-bold uppercase tracking-widest text-white">
            GitHub connected
          </h2>
          <p className="font-mono text-[10px] font-bold uppercase tracking-widest text-emerald-400">
            The GitHub App is installed and ready.
          </p>
        </div>
      </div>

      <div>
        <h3 className="font-mono text-[10px] font-bold uppercase tracking-widest text-slate-400">
          Select repositories
        </h3>
        <p className="mt-1 text-sm leading-6 text-slate-400">
          Choose which repositories Arti should index. You can change this later
          in Settings.
        </p>
      </div>

      {repoError && (
        <Alert variant="warning" animate>
          {repoError}
        </Alert>
      )}

      {loading && (
        <div className="space-y-2">
          {Array.from({ length: 3 }, (_, i) => (
            <div
              key={i}
              className="flex items-center gap-3 border border-slate-700 bg-slate-800 px-3 py-2"
            >
              <div className="h-4 w-4 animate-pulse bg-slate-700" />
              <div
                className="h-3 flex-1 animate-pulse bg-slate-700"
                style={{ animationDelay: `${i * 100}ms` }}
              />
            </div>
          ))}
        </div>
      )}
      {!loading && repos.length > 0 && (
        <div className="space-y-1">
          <label className="flex cursor-pointer select-none items-center gap-2 border border-slate-700 bg-arti-background px-3 py-2 transition-colors hover:border-slate-600">
            <input
              type="checkbox"
              checked={selected.size === repos.length}
              onChange={toggleAll}
              className="h-4 w-4 border-slate-600 bg-arti-background text-emerald-500 focus:ring-2 focus:ring-emerald-500/30"
            />
            <span className="font-mono text-[10px] font-bold uppercase tracking-widest text-slate-400">
              {selected.size === repos.length ? "Deselect all" : "Select all"}
            </span>
          </label>

          <div className="max-h-64 space-y-1 overflow-y-auto">
            {repos.map((repo) => (
              <label
                key={repo.id}
                className="flex cursor-pointer select-none items-center gap-2 border border-slate-700 bg-arti-background px-3 py-2 transition-colors hover:border-slate-600"
              >
                <input
                  type="checkbox"
                  checked={selected.has(repo.id)}
                  onChange={() => toggle(repo.id)}
                  className="h-4 w-4 border-slate-600 bg-arti-background text-emerald-500 focus:ring-2 focus:ring-emerald-500/30"
                />
                <span className="truncate font-mono text-sm text-slate-300">
                  {repo.name}
                </span>
              </label>
            ))}
          </div>
        </div>
      )}
      {!loading && repos.length === 0 && (
        <div className="border border-slate-700 bg-arti-background p-4 text-center">
          <span className="font-mono text-lg text-slate-600">□</span>
          <p className="mt-1 font-mono text-[10px] text-slate-500">
            No repositories found. You can add them later in Settings.
          </p>
        </div>
      )}

      <Button
        onClick={() => void handleSave()}
        loading={saving}
        loadingText="Adding repositories..."
        fullWidth
      >
        {selected.size > 0
          ? `Add ${selected.size} ${selected.size === 1 ? "repository" : "repositories"} & continue`
          : "Continue"}
      </Button>

      {repos.length > 0 && (
        <Button onClick={onContinue} variant="ghost" fullWidth>
          Skip for now
        </Button>
      )}
    </div>
  );
};
